import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';

const schemes = [
  {
    title: 'Basic Chit Fund',
    description: 'Entry-level plan for individuals starting their chit journey, ideal for first-time subscribers.',
    details: '₹1,00,000 | 20 months | ₹5,000/month',
  },
  {
    title: 'Standard Chit Fund',
    description: 'A balanced plan for members seeking steady returns and manageable monthly contributions.',
    details: '₹2,00,000 | 25 months | ₹8,000/month',
    planPdf: '/Chit1.pdf',
  },
  {
    title: 'Premium Chit Fund',
    description: 'Higher-value scheme designed for members who want strong liquidity and competitive bidding.',
    details: '₹3,00,000 | 30 months | ₹10,000/month',
  },
  {
    title: 'Gold Chit Fund',
    description: 'Premium membership with bigger fund value and enhanced bidding power.',
    details: '₹5,00,000 | 40 months | ₹12,500/month',
    planPdf: '/Chit2.pdf',
    disclaimer: 'The bid data presented is based on past auctions. Profits are subject to change and may increase or decrease depending on future bid amounts and member participation.',
  },
  {
    title: 'Platinum Chit Fund',
    description: 'For high-value investors who want faster results and premium support.',
    details: '₹5,00,000 | 25 months | ₹20,000/month',
    planPdf: '/Chit2.pdf',
    disclaimer: 'The bid data presented is based on past auctions. Profits are subject to change and may increase or decrease depending on future bid amounts and member participation.',
  },
];

const SchemesPage: React.FC = () => {
  const [isApplyModalOpen, setIsApplyModalOpen] = useState(false);
  const [selectedScheme, setSelectedScheme] = useState('');

  const openApplyModal = (schemeName: string) => {
    setSelectedScheme(schemeName);
    setIsApplyModalOpen(true);
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-indigo-50 via-violet-50 to-amber-50 pt-24 pb-20">
      <Header />
      <div className="flex-1 w-full px-4 max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-10">
          <div>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">Chit Fund Schemes</h1>
            <p className="text-lg text-slate-700 max-w-2xl">Choose from our curated plans designed for safety, growth and guaranteed member benefits with transparent charges.</p>
          </div>
          <Link to="/" className="inline-flex items-center px-6 py-3 rounded-full bg-gradient-to-r from-amber-400 via-amber-500 to-yellow-400 text-slate-950 font-semibold shadow-lg hover:from-amber-500 hover:to-yellow-500 transition">
            Back to Home
          </Link>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {schemes.map((scheme) => (
            <div
              key={scheme.title}
              onClick={() => openApplyModal(`${scheme.title} - ${scheme.details}`)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  openApplyModal(`${scheme.title} - ${scheme.details}`);
                }
              }}
              role="button"
              tabIndex={0}
              className="rounded-3xl border border-slate-200 bg-white p-8 shadow-xl hover:-translate-y-1 transition-transform duration-300 cursor-pointer focus:outline-none focus:ring-4 focus:ring-[#D4A574]/40"
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-slate-900">{scheme.title}</h2>
                <span className="rounded-full bg-indigo-100 px-4 py-2 text-indigo-700 text-sm font-semibold">Popular</span>
              </div>
              <p className="text-slate-600 leading-relaxed mb-6">{scheme.description}</p>
              <p className="text-sm font-semibold uppercase text-slate-500 tracking-wider">Plan details</p>
              <p className="text-xl font-semibold text-slate-900 mt-2">{scheme.details}</p>
              {scheme.planPdf && (
                <div className="mt-6">
                  <a
                    href={scheme.planPdf}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(event) => event.stopPropagation()}
                    className="inline-flex items-center justify-center rounded-full bg-[#056160] px-6 py-3 text-sm font-bold text-white shadow-lg transition hover:bg-[#044c4c] focus:outline-none focus:ring-2 focus:ring-[#D4A574] focus:ring-offset-2"
                  >
                    View Plan
                  </a>
                  {scheme.disclaimer && (
                    <p className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm leading-relaxed text-slate-700">
                      {scheme.disclaimer}
                    </p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {isApplyModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto bg-black/60 px-3 py-4 backdrop-blur-sm sm:items-center sm:px-4 sm:py-8">
          <div className="relative w-full max-w-md rounded-2xl border border-teal-100 bg-white p-5 shadow-2xl shadow-[0_0_50px_rgba(5,97,96,0.3)] sm:rounded-3xl sm:p-8">
            <button onClick={() => setIsApplyModalOpen(false)} className="absolute right-3 top-3 flex h-10 w-10 items-center justify-center rounded-full text-3xl font-bold leading-none text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-800 sm:right-4 sm:top-4" aria-label="Close application form">&times;</button>
            <h3 className="pr-10 text-xl font-bold text-[#056160] sm:text-2xl">Apply for Scheme</h3>
            <p className="mb-5 mt-3 rounded-lg border border-emerald-100 bg-emerald-50 p-3 text-sm font-semibold leading-relaxed text-gray-600 sm:mb-6 sm:text-base">{selectedScheme}</p>

            <form action="https://formsubmit.co/info@novatrust.co.in" method="POST" className="space-y-3 sm:space-y-4">
              <input type="hidden" name="_captcha" value="false" />
              <input type="hidden" name="_template" value="table" />
              <input type="hidden" name="Scheme_Applied" value={selectedScheme} />
              <input type="hidden" name="_subject" value="New Chit Fund Application" />
              <input type="hidden" name="_redirect" value="https://novatrust.co.in/" />
              <div>
                <label className="mb-1 block text-sm font-semibold text-gray-700 sm:text-base">Full Name</label>
                <input type="text" name="name" required className="w-full rounded-xl border border-gray-300 px-4 py-3 text-base outline-none transition-all placeholder-gray-400 focus:border-[#056160] focus:ring-2 focus:ring-[#056160]" placeholder="Your Name" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-semibold text-gray-700 sm:text-base">Email Address</label>
                <input type="email" name="email" required className="w-full rounded-xl border border-gray-300 px-4 py-3 text-base outline-none transition-all placeholder-gray-400 focus:border-[#056160] focus:ring-2 focus:ring-[#056160]" placeholder="youremail@example.com" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-semibold text-gray-700 sm:text-base">Phone Number</label>
                <input type="tel" name="phone" required className="w-full rounded-xl border border-gray-300 px-4 py-3 text-base outline-none transition-all placeholder-gray-400 focus:border-[#056160] focus:ring-2 focus:ring-[#056160]" placeholder="+91 XXXXX XXXXX" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-semibold text-gray-700 sm:text-base">Purpose of Joining</label>
                <select name="Purpose_of_Joining" required defaultValue="" className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-base text-gray-700 outline-none transition-all focus:border-[#056160] focus:ring-2 focus:ring-[#056160]">
                  <option value="" disabled>Select purpose</option>
                  <option value="Borrowing">Borrowing</option>
                  <option value="Saving and Returns">Saving and Returns</option>
                </select>
              </div>
              <button type="submit" className="mt-4 w-full rounded-xl bg-gradient-to-r from-[#056160] to-teal-800 py-4 text-base font-bold text-white shadow-lg transition-all duration-300 hover:-translate-y-1 hover:to-teal-900 hover:shadow-xl">
                Submit Application
              </button>
            </form>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default SchemesPage;
